import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type {
  CreateWorkflowAutomationConditionRequestModel,
  WorkflowAutomationConditionListModel,
  WorkflowAutomationConditionModel,
} from '../models/automation-api.models';
import { asArray, asRecord, asString } from './automation-api.utils';

const CONDITION_FIELD_FROM_INT = {
  1: 'CardField',
  2: 'CardStage',
  3: 'ChecklistCompleted', 4: 'CardComments',
} as const;

const CONDITION_OP_FROM_INT = {
  1: 'Equals',
  2: 'NotEquals',
  3: 'GreaterThan',
  4: 'LessThan',
  5: 'Contains',
  6: 'GreaterThanOrEqual',
  7: 'LessThanOrEqual',
  8: 'NotContains',
  9: 'IsEmpty',
  10: 'IsNotEmpty',
  11: 'IsTrue',
  12: 'IsFalse',
  20: 'ChangedTo',
  21: 'ChangedFromTo',
  22: 'BecameFilled',
  23: 'BecameEmpty',
  30: 'DateIsToday',
  31: 'DateIsOverdue',
  32: 'DateIsWithinDays',
} as const;

const CONDITION_FIELD_TO_INT = {
  CardField: 1,
  CardStage: 2,
  ChecklistCompleted: 3, CardComments: 4,
} as const;

const CONDITION_OP_TO_INT = {
  Equals: 1,
  NotEquals: 2,
  GreaterThan: 3,
  LessThan: 4,
  Contains: 5,
  GreaterThanOrEqual: 6,
  LessThanOrEqual: 7,
  NotContains: 8,
  IsEmpty: 9,
  IsNotEmpty: 10,
  IsTrue: 11,
  IsFalse: 12,
  ChangedTo: 20,
  ChangedFromTo: 21,
  BecameFilled: 22,
  BecameEmpty: 23,
  DateIsToday: 30,
  DateIsOverdue: 31,
  DateIsWithinDays: 32,
} as const;

const CONDITION_OP_FROM_STRING: Record<string, WorkflowAutomationConditionModel['operator']> = Object.fromEntries(
  Object.keys(CONDITION_OP_TO_INT).map(k => [k.toLowerCase(), k as WorkflowAutomationConditionModel['operator']])
);

@Injectable({ providedIn: 'root' })
export class WorkflowAutomationConditionsApiService {
  private readonly http = inject(HttpClient);

  async list(workflowId: string, automationId: string): Promise<WorkflowAutomationConditionListModel> {
    const response = await firstValueFrom(
      this.http.get(`/api/workflows/${workflowId}/automations/${automationId}/conditions`)
    );
    const items = this.extractItems(response);
    return {
      items: items.map((entry) => this.mapCondition(entry, automationId)),
    };
  }

  async create(
    workflowId: string,
    automationId: string,
    payload: CreateWorkflowAutomationConditionRequestModel
  ): Promise<WorkflowAutomationConditionModel | null> {
    const response = await firstValueFrom(
      this.http.post(
        `/api/workflows/${workflowId}/automations/${automationId}/conditions`,
        this.toApiPayload(payload)
      )
    );
    const mapped = this.mapCondition(response, automationId);
    return mapped.id ? mapped : null;
  }

  async delete(workflowId: string, automationId: string, conditionId: string): Promise<void> {
    await firstValueFrom(
      this.http.delete(`/api/workflows/${workflowId}/automations/${automationId}/conditions/${conditionId}`)
    );
  }

  private mapCondition(raw: unknown, automationId: string): WorkflowAutomationConditionModel {
    const source = asRecord(raw);
    const fieldRaw = source['field'] ?? source['Field'];
    const operatorRaw = source['operator'] ?? source['Operator'];
    const field = typeof fieldRaw === 'number'
      ? (CONDITION_FIELD_FROM_INT[fieldRaw as keyof typeof CONDITION_FIELD_FROM_INT] ?? 'CardField')
      : this.normalizeField(asString(fieldRaw));
    const operator = typeof operatorRaw === 'number'
      ? (CONDITION_OP_FROM_INT[operatorRaw as keyof typeof CONDITION_OP_FROM_INT] ?? 'Equals')
      : this.normalizeOperator(asString(operatorRaw));
    return {
      id: asString(source['id'] ?? source['Id']),
      workflowAutomationId:
        asString(source['workflowAutomationId'] ?? source['WorkflowAutomationId']) || automationId,
      field,
      operator,
      value: this.parseConditionValue(field, source['value'] ?? source['Value']),
      createdAt: asString(source['createdAt'] ?? source['CreatedAt']),
    };
  }

  private extractItems(response: unknown): unknown[] {
    if (Array.isArray(response)) return response;
    const source = asRecord(response);
    return asArray(source['items'] ?? source['Items']);
  }

  private normalizeField(value: string): WorkflowAutomationConditionModel['field'] {
    const trimmed = value.trim();
    if (trimmed === 'CardField' || trimmed === 'CardStage' || trimmed === 'ChecklistCompleted' || trimmed === 'CardComments') {
      return trimmed;
    }
    const numeric = Number(trimmed);
    if (Number.isFinite(numeric)) {
      return CONDITION_FIELD_FROM_INT[numeric as keyof typeof CONDITION_FIELD_FROM_INT] ?? 'CardField';
    }
    const lower = trimmed.toLowerCase();
    if (lower === 'cardfield') return 'CardField';
    if (lower === 'cardstage') return 'CardStage';
    if (lower === 'cardcomments') return 'CardComments';
    if (lower === 'checklistcompleted') return 'ChecklistCompleted';
    return 'CardField';
  }

  private normalizeOperator(value: string): WorkflowAutomationConditionModel['operator'] {
    const trimmed = value.trim();
    if (!trimmed) return 'Equals';
    const numeric = Number(trimmed);
    if (Number.isFinite(numeric)) {
      return CONDITION_OP_FROM_INT[numeric as keyof typeof CONDITION_OP_FROM_INT] ?? 'Equals';
    }
    return CONDITION_OP_FROM_STRING[trimmed.toLowerCase()] ?? 'Equals';
  }

  private toApiPayload(payload: CreateWorkflowAutomationConditionRequestModel): {
    field: number;
    operator: number;
    value: string;
  } {
    return {
      field: CONDITION_FIELD_TO_INT[payload.field] ?? 1,
      operator: CONDITION_OP_TO_INT[payload.operator] ?? 1,
      value: this.serializeConditionValue(payload),
    };
  }

  private serializeConditionValue(payload: CreateWorkflowAutomationConditionRequestModel): string {
    if (payload.field === 'CardStage') {
      return typeof payload.value === 'string' ? payload.value : String(payload.value ?? '');
    }

    if (payload.field === 'ChecklistCompleted') {
      if (typeof payload.value === 'boolean') {
        return payload.value ? 'true' : 'false';
      }
      const raw = String(payload.value ?? '').trim().toLowerCase();
      return raw === 'false' ? 'false' : 'true';
    }

    if (typeof payload.value === 'string') {
      return payload.value;
    }

    return JSON.stringify(payload.value ?? '');
  }

  private parseConditionValue(
    field: WorkflowAutomationConditionModel['field'],
    rawValue: unknown
  ): unknown {
    if (field === 'ChecklistCompleted') {
      if (typeof rawValue === 'boolean') return rawValue;
      const normalized = String(rawValue ?? '').trim().toLowerCase();
      if (normalized === 'true') return true;
      if (normalized === 'false') return false;
      return rawValue;
    }

    if (field !== 'CardField' && field !== 'CardComments') {
      return rawValue;
    }

    if (rawValue && typeof rawValue === 'object') {
      return rawValue;
    }

    if (typeof rawValue !== 'string') {
      return rawValue;
    }

    const trimmed = rawValue.trim();
    if (!trimmed) return { workflowFieldId: '', value: '' };

    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch {}

    return rawValue;
  }
}
